export interface Disposable {
  dispose(): void;
}

export interface ReadonlyObservable<T> {
  readonly value: T;
  subscribe(listener: (value: T) => void): () => void;
}

export interface ObservableOptions<T> {
  equals?: (a: T, b: T) => boolean;
  normalize?: (v: T) => T;
  onChange?: (v: T) => void;
}

/**
 * Minimal reactive value holder. The order of `onChange` (internal side-effects)
 * BEFORE the listener notification is load-bearing: components owned by the
 * holder (renderer, audio nodes, ...) must observe the new value before
 * external React subscribers re-render.
 */
export class Observable<T> implements ReadonlyObservable<T>, Disposable {
  private _value: T;
  private listeners = new Set<(v: T) => void>();

  constructor(initial: T, private readonly opts: ObservableOptions<T> = {}) {
    this._value = opts.normalize ? opts.normalize(initial) : initial;
  }

  get value(): T {
    return this._value;
  }

  set(next: T): boolean {
    const normalized = this.opts.normalize ? this.opts.normalize(next) : next;
    const eq = this.opts.equals ?? Object.is;

    if (eq(this._value, normalized)) {
      return false;
    }

    this._value = normalized;
    this.opts.onChange?.(normalized);
    this.listeners.forEach((cb) => cb(normalized));

    return true;
  }

  subscribe(listener: (value: T) => void): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose(): void {
    this.listeners.clear();
  }
}
